/**
 * Re-request an ArchiPro image-handler URL at a larger size. Project/product
 * tiles bake a small `boxWidth` into the transform (e.g. 804px), which is sharp
 * in a tile but blurs when blown up into the full-bleed feature image. The
 * source is high-res, so scaling the `zpcf` box up to `targetWidth` (default
 * 1920, matching the hero banners) yields a crisp crop with no extra request
 * on the backend.
 *
 * Returns the URL untouched when it isn't a parseable handler URL or is already
 * at least `targetWidth` wide.
 */
export const upscaleImageHandlerUrl = (
    url: string | undefined,
    targetWidth = 1920,
): string | undefined => {
    if (!url) return url;
    const match = url.match(
        /^(.*\.(?:jpe?g|png|webp))\/([A-Za-z0-9+/=]+)(\?.*)?$/i,
    );
    if (!match) return url;
    const path = match[1];
    const base64 = match[2];
    const query = match[3] ?? '';
    if (!path || !base64) return url;
    try {
        const config = JSON.parse(atob(base64));
        const options = config?.edits?.[0]?.options;
        if (!options?.boxWidth || options.boxWidth >= targetWidth) {
            return url;
        }
        const factor = targetWidth / options.boxWidth;
        options.boxWidth = Math.round(options.boxWidth * factor);
        options.boxHeight = Math.round(options.boxHeight * factor);
        if (options.zoomWidth) {
            options.zoomWidth = Math.round(options.zoomWidth * factor);
        }
        return `${path}/${btoa(JSON.stringify(config))}${query}`;
    } catch {
        return url;
    }
};
